/**
 * InvestigationFeedback — Sprint 12 Phase A.
 *
 * Compact thumb-up/down + tag widget that mounts under both /investigate/:id
 * and /issues/:id (the latter rates the analysis, not the issue itself). The
 * widget is deliberately small and dense — engineers triage many cases per
 * day, so the friction to leave feedback must be near zero.
 *
 * Interaction:
 *   1. Two IconButtons (thumb-up / thumb-down). Click submits immediately —
 *      no Save button. The selected button switches from Outlined to filled.
 *   2. Once a thumb is selected the chip row reveals (seven allowed tags).
 *      Clicking a chip toggles it and submits the new tags list. Multi-select.
 *   3. Below the chips, an "Add a note" trigger expands a textarea on focus.
 *      Saved on blur. Optional, max 2000 chars.
 *   4. On mount, GET /api/feedback/me/{source_type}/{source_id}/ pre-loads
 *      the caller's existing row so reopening the page shows the prior state.
 *
 * Design notes (ui-ux-pro-max applied):
 *   - 8dp spacing: gap={1}/{2}, sx={{ p: 2 }} on the outer wrapper.
 *   - Touch targets: IconButton size="large" (48px) — exceeds 44px floor.
 *   - Accessibility: aria-label on each icon button + aria-pressed on the
 *     active thumb; the chip row carries role="group" + aria-label.
 *   - Density: variant="outlined" Chip + size="small" everywhere.
 *   - Three-state rule: loading skeleton on initial fetch, error Alert on
 *     submit failure (inline, dismiss on next successful submit).
 *   - Toast: dispatches the shared 'sherlock:toast' CustomEvent on every
 *     successful submit (matches the DeleteInvestigationDialog pattern).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { feedback as feedbackApi, ApiError } from '@/helpers/ApiHelper';
import type {
  FeedbackOut,
  FeedbackSourceType,
  FeedbackTag,
  FeedbackThumb,
} from '@/types/feedback';
import { ALLOWED_FEEDBACK_TAGS, FEEDBACK_TAG_LABELS } from '@/types/feedback';

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface InvestigationFeedbackProps {
  /** Which kind of source we're rating. */
  sourceType: FeedbackSourceType;
  /** UUID of the investigation or analysis. */
  sourceId: string;
}

// ---------------------------------------------------------------------------
// Toast helper — fires the shared "sherlock:toast" CustomEvent the
// EasterEggs / global handler listens for. Kept local so the component
// doesn't grow a dependency on the snackbar implementation.
// ---------------------------------------------------------------------------

function fireToast(message: string): void {
  window.dispatchEvent(
    new CustomEvent('sherlock:toast', { detail: { message } }),
  );
}

const NOTE_MAX_LENGTH = 2000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InvestigationFeedback({
  sourceType,
  sourceId,
}: InvestigationFeedbackProps) {
  // Initial load — independent of any submit. ``loading`` is true only
  // during the first GET; subsequent submits use ``submitting`` so the UI
  // doesn't flicker into a skeleton on every chip toggle.
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The current persisted row (mirrors the server). null while loading or
  // when the user has not yet rated.
  const [row, setRow] = useState<FeedbackOut | null>(null);

  // Local-only typing buffer; persisted via handleNoteBlur.
  const [noteDraft, setNoteDraft] = useState<string>('');
  const [noteExpanded, setNoteExpanded] = useState(false);

  // Latest in-flight request abort token — guards against state mutations
  // when the parent unmounts while a fetch is mid-air.
  const abortedRef = useRef(false);
  useEffect(() => {
    abortedRef.current = false;
    return () => {
      abortedRef.current = true;
    };
  }, []);

  // ------- Initial load -------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    feedbackApi
      .getMine(sourceType, sourceId)
      .then((existing) => {
        if (cancelled) return;
        setRow(existing);
        setNoteDraft(existing?.note ?? '');
        setNoteExpanded(Boolean(existing?.note));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const detail =
          err instanceof ApiError
            ? err.detail
            : 'Could not load feedback. Reload to retry.';
        setError(detail);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceType, sourceId]);

  // ------- Submit one upsert -------
  const submit = useCallback(
    async ({
      thumb,
      tags,
      note,
    }: {
      thumb: FeedbackThumb;
      tags: FeedbackTag[];
      note: string | null;
    }): Promise<void> => {
      setSubmitting(true);
      setError(null);
      try {
        const saved = await feedbackApi.upsert({
          source_type: sourceType,
          source_id: sourceId,
          thumb,
          tags,
          note: note && note.trim() !== '' ? note : null,
        });
        if (abortedRef.current) return;
        setRow(saved);
        fireToast('Feedback saved');
      } catch (err: unknown) {
        if (abortedRef.current) return;
        const detail =
          err instanceof ApiError
            ? err.detail
            : 'Could not save feedback. Please try again.';
        setError(detail);
      } finally {
        if (!abortedRef.current) setSubmitting(false);
      }
    },
    [sourceType, sourceId],
  );

  // ------- Thumb click -------
  const handleThumb = useCallback(
    (next: FeedbackThumb) => {
      if (submitting) return;
      // Same direction → no-op. Toggling between -1/+1 keeps tags + note so
      // the user doesn't have to re-enter them when correcting a mistake.
      if (row && row.thumb === next) return;
      void submit({
        thumb: next,
        tags: row?.tags ?? [],
        note: row?.note ?? null,
      });
    },
    [row, submit, submitting],
  );

  // ------- Chip toggle -------
  const handleToggleTag = useCallback(
    (tag: FeedbackTag) => {
      if (submitting || row === null) return;
      const current = row.tags ?? [];
      const next = current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag];
      void submit({ thumb: row.thumb, tags: next, note: row.note });
    },
    [row, submit, submitting],
  );

  // ------- Note input -------
  // Update local buffer on every keystroke; persistence is deferred to
  // onBlur (below) so the user can write a full sentence without the
  // mid-stream submit/disabled flip kicking focus out of the textarea.
  const handleNoteChange = useCallback((next: string) => {
    setNoteDraft(next.slice(0, NOTE_MAX_LENGTH));
  }, []);

  const handleNoteBlur = useCallback(() => {
    if (row === null) return; // need a thumb selected before persisting a note
    if (abortedRef.current) return;
    const clipped = noteDraft.slice(0, NOTE_MAX_LENGTH);
    if ((row.note ?? '') === clipped) return; // no-op
    void submit({ thumb: row.thumb, tags: row.tags, note: clipped });
  }, [row, noteDraft, submit]);

  // ------- Loading state -------
  if (loading) {
    return (
      <Box sx={{ p: 2 }} role="status" aria-label="Loading feedback widget">
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="rectangular" width={120} height={24} />
        </Stack>
      </Box>
    );
  }

  // ------- Main render -------
  const currentThumb = row?.thumb ?? null;
  const currentTags = row?.tags ?? [];

  return (
    <Box
      sx={{ p: 2 }}
      role="region"
      aria-label={`Feedback for this ${sourceType}`}
    >
      <Stack spacing={1.5}>
        {/* Header line */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Was this {sourceType} useful?
        </Typography>

        {/* Thumb row */}
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            aria-label="Mark this as useful"
            aria-pressed={currentThumb === 1}
            size="large"
            onClick={() => handleThumb(1)}
            disabled={submitting}
            sx={{
              color: currentThumb === 1 ? 'success.main' : 'text.secondary',
            }}
          >
            {currentThumb === 1 ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
          </IconButton>
          <IconButton
            aria-label="Mark this as not useful"
            aria-pressed={currentThumb === -1}
            size="large"
            onClick={() => handleThumb(-1)}
            disabled={submitting}
            sx={{
              color: currentThumb === -1 ? 'error.main' : 'text.secondary',
            }}
          >
            {currentThumb === -1 ? (
              <ThumbDownIcon />
            ) : (
              <ThumbDownOutlinedIcon />
            )}
          </IconButton>

          {/* Subtle "saved" hint replaces the absence of an explicit Save button. */}
          {row !== null && !submitting && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ ml: 1 }}
              aria-live="polite"
            >
              Saved
            </Typography>
          )}
          {submitting && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ ml: 1 }}
              aria-live="polite"
            >
              Saving…
            </Typography>
          )}
        </Stack>

        {/* Error banner */}
        {error !== null && (
          <Alert severity="error" role="alert" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Chip row — only visible once a thumb is picked, so the widget
            stays minimal for users who only want to leave a thumb. */}
        {row !== null && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            role="group"
            aria-label="Reaction tags"
          >
            {ALLOWED_FEEDBACK_TAGS.map((tag) => {
              const selected = currentTags.includes(tag);
              return (
                <Chip
                  key={tag}
                  label={FEEDBACK_TAG_LABELS[tag]}
                  variant={selected ? 'filled' : 'outlined'}
                  color={selected ? 'primary' : 'default'}
                  size="small"
                  onClick={() => handleToggleTag(tag)}
                  aria-pressed={selected}
                  disabled={submitting}
                  sx={{
                    minHeight: 28,
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </Stack>
        )}

        {/* Note trigger / textarea — collapsed by default; expands on focus.
            Persists when the user clicks/tabs outside the textarea. */}
        {row !== null && !noteExpanded && (
          <Box>
            <Typography
              component="button"
              type="button"
              variant="body2"
              onClick={() => setNoteExpanded(true)}
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'primary.main',
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: 500,
                textAlign: 'left',
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              + Add a note
            </Typography>
          </Box>
        )}
        {row !== null && noteExpanded && (
          <TextField
            label="Note"
            id={`feedback-note-${sourceId}`}
            placeholder="What worked, what didn't, what was missing…"
            multiline
            minRows={1}
            maxRows={5}
            fullWidth
            size="small"
            value={noteDraft}
            onChange={(e) => handleNoteChange(e.target.value)}
            onBlur={handleNoteBlur}
            inputProps={{ maxLength: NOTE_MAX_LENGTH }}
            helperText={`${noteDraft.length}/${NOTE_MAX_LENGTH} — saved when you click outside`}
          />
        )}
      </Stack>
    </Box>
  );
}
